import { lazy, Suspense, useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react'
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import Sidebar from './components/Sidebar'
import TopBar from './components/TopBar'
import MobileNav from './components/MobileNav'
import ErrorBoundary from './components/ErrorBoundary'
import { Icon } from './components/ui/icons'
import {
  getAnalysisStatus, getCivicFeedContext,
  getCivicSyncHistory, getCivicSyncStatus, getCTASummary, getDistrictContext, getLLMSettings,
  getQuestionnaireDefinition, getQuestionnaireResponses, getSettings,
  getSystemInfo, saveAddress, prefillAddressByCoordinates, saveCivicSettings,
  saveLLMSettings, syncCivic, testLLMConnection,
  updateProfile, logout, deleteAccount, getNotificationsSummary, setUnauthorizedHandler,
  authMe, changePassword, completeAppleWebSignIn, startAppleWebSignIn, ApiError,
} from './lib/api'
import { isAuthenticated, clearToken, getToken, onAuthChange } from './lib/auth'
import LoginView from './views/LoginView'
import type {
  AnalysisStatus,
  CivicSyncRun, CivicSyncStatus, CTASummary, NotificationSummaryCoordinator, NotificationsSummary,
  DistrictContextResponse, LLMSettings, QuestionnaireDefinition, QuestionnaireResponseResult,
  Entitlement, QuestionnaireResponses, SystemInfo, UserAddress, UserSettings,
} from './lib/types'
import {
  DEFAULT_ADDRESS,
  hasAddressConfigured, getDistrictSummary,
} from './views/shared'
import type { ThemePreference, ThemeResolution } from './lib/theme'
import { applyTheme, resolveThemePreference, saveThemePreference, watchSystemTheme } from './lib/theme'
import { billListReturnIsPending, captureInAppBillOrigin, clearBillListReturnMemory } from './lib/billListReturn'
import { ApiSessionProvider, clearPrivateSessionStorage, setActiveApiSession, type ApiSession } from './lib/sessionFence'
import { getShellPresentation } from './lib/shellNavigation'
import { RouteBackProvider, useRouteBack, useRouteBackRegistration } from './lib/routeBackRegistry'
import { createRouteProvenanceSession, RouteProvenanceProvider, type RouteProvenanceSession } from './lib/routeProvenance'
import { availableSessionStorage, publishQuestionnaireSubmitInvalidation, questionnaireDraftSavedAt, questionnaireResponseFingerprint, readQuestionnaireSubmitInvalidation, removeQuestionnaireDraft, removeQuestionnaireDraftAtOrBefore, validQuestionnaireAnswers, writeQuestionnaireDraft } from './lib/questionnaireDraft'

import RouteLoadingFallback from './routes/RouteLoadingFallback'

const DashboardView = lazy(() => import('./views/DashboardView'))
const ProfileView = lazy(() => import('./views/ProfileView'))
const RepresentativeListView = lazy(() => import('./views/RepresentativeListView'))
const BillListView = lazy(() => import('./views/BillListView'))
const NominationsView = lazy(() => import('./views/NominationsView'))
const CommitteesView = lazy(() => import('./views/CommitteesView'))
const VotesView = lazy(() => import('./views/VotesView'))
const CalendarView = lazy(() => import('./views/CalendarView'))
const ResearchView = lazy(() => import('./views/ResearchView'))
const CTAListView = lazy(() => import('./views/CTAListView'))
const SettingsView = lazy(() => import('./views/SettingsView'))
const BillDetailRoute = lazy(() => import('./routes/BillDetailRoute'))
const RollCallRoute = lazy(() => import('./routes/RollCallRoute'))
const RepresentativeDetailRoute = lazy(() => import('./routes/RepresentativeDetailRoute'))
const CtaDetailRoute = lazy(() => import('./routes/CtaDetailRoute'))
const BadgesView = lazy(() => import('./views/BadgesView'))
const TopicsView = lazy(() => import('./views/TopicsView'))
const NotificationsView = lazy(() => import('./views/NotificationsView'))
const QuestionnaireFlowView = lazy(() => import('./views/QuestionnaireFlowView'))

interface TabMeta { title: string; subtitle: string }

function SessionRouteBack({ ownerKey, fallback, provenance }: { ownerKey: string; fallback: string; provenance: RouteProvenanceSession }) {
  const navigate = useNavigate()
  const location = useLocation()
  const goBack = useCallback(() => {
    if (provenance.hasPredecessor(location.key)) navigate(-1)
    else navigate(fallback, { replace: true })
  }, [fallback, location.key, navigate, provenance])
  useRouteBackRegistration(ownerKey, goBack)
  return null
}

function QuestionnaireRouteBack({ ownerKey, provenance }: { ownerKey: string; provenance: RouteProvenanceSession }) {
  const navigate = useNavigate()
  const location = useLocation()
  const close = useCallback(() => {
    let key = location.key
    let depth = 0
    const seen = new Set<string>()
    let predecessor = provenance.predecessor(key)
    while (predecessor && predecessor.pathname === '/profile/values/questionnaire' && !seen.has(predecessor.key)) {
      seen.add(key)
      key = predecessor.key
      depth += 1
      predecessor = provenance.predecessor(key)
    }
    if (predecessor && !seen.has(predecessor.key)) navigate(-(depth + 1))
    else navigate('/profile/values', { replace: true })
  }, [location.key, navigate, provenance])
  useRouteBackRegistration(ownerKey, close)
  return null
}

function AppleWebCompleteRoute() {
  const location = useLocation()
  const [error, setError] = useState<string | null>(null)
  const [canCreateNewAccount, setCanCreateNewAccount] = useState(false)
  const [restarting, setRestarting] = useState(false)

  useEffect(() => {
    let cancelled = false
    const transaction = new URLSearchParams(location.search).get('transaction') || ''
    if (!transaction) {
      setError('Apple sign-in expired. Please start again from the sign-in page.')
      return () => { cancelled = true }
    }
    completeAppleWebSignIn(transaction)
      .then(() => {
        if (!cancelled) window.location.replace('/')
      })
      .catch((err) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Apple sign-in failed. Please start again.'
        try {
          const parsed = JSON.parse(message)
          const detail = typeof parsed.detail === 'string' ? parsed.detail : null
          const code = typeof parsed.code === 'string' ? parsed.code : null
          if (code === 'registration_disabled') setError('New registrations are unavailable. Sign in with an existing account.')
          else if (code === 'apple_identity_conflict') setError('This account already uses a different Apple identity. Use the originally connected Apple account or another sign-in method.')
          else if (code === 'apple_new_account_confirmation_required') {
            setCanCreateNewAccount(true)
            setError('This Apple identity was previously deleted. Old data will not be restored. You may start a fresh Apple sign-in only if you want to create a new account.')
          }
          else setError(detail || 'Apple sign-in failed. Please start again.')
        } catch {
          setError(message)
        }
      })
    return () => { cancelled = true }
  }, [location.search])

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">Completing Apple sign-in</h1>
        {error ? (
          <>
            <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>
            <div className="mt-6 flex flex-col items-center gap-3">
              {canCreateNewAccount && (
                <button
                  type="button"
                  disabled={restarting}
                  onClick={() => {
                    setRestarting(true)
                    startAppleWebSignIn(true).catch((err) => {
                      setRestarting(false)
                      setError(err instanceof Error ? err.message : 'Could not restart Apple sign-in.')
                    })
                  }}
                  className="inline-flex rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
                >
                  {restarting ? 'Opening Apple...' : 'Create a new account with Apple'}
                </button>
              )}
              <a className="inline-flex rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700" href="/">
                Return to sign in
              </a>
            </div>
          </>
        ) : (
          <p className="mt-3 text-sm text-slate-600">Keep this tab open. We are verifying your Apple authorization.</p>
        )}
      </div>
    </div>
  )
}

function ShellBackButton({ ownerKey, label = 'Back' }: { ownerKey: string; label?: string }) {
  const { invokeRouteBack } = useRouteBack()
  return (
    <button
      type="button"
      onClick={() => invokeRouteBack(ownerKey)}
      className="min-h-11 rounded-md p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      aria-label={label}
    >
      ←
    </button>
  )
}

const TAB_META: Record<string, TabMeta> = {
  dashboard: { title: 'Home', subtitle: 'Your civic dashboard' },
  bills: { title: 'Bills', subtitle: 'Federal, state, county, and municipal legislation affecting your district' },
  nominations: { title: 'Nominations', subtitle: 'Presidential nominations to federal positions' },
  committees: { title: 'Committee Activity', subtitle: 'Committee meetings and hearings' },
  votes: { title: 'Votes', subtitle: 'How chambers and councils voted on synced measures' },
  calendar: { title: 'Calendar', subtitle: 'Federal and local civic events and milestones' },
  actions: { title: 'Actions', subtitle: 'Bills that affect your values' },
  research: { title: 'Research & Reports', subtitle: 'Independent research and Congressional Research Service reports' },
  badges: { title: 'Civic Badges', subtitle: 'Earn recognition for civic engagement' },
  settings: { title: 'Settings', subtitle: 'Configure your Constituent experience' },
  profile: { title: 'Profile', subtitle: 'Your civic profile' },
  topics: { title: 'Topics', subtitle: 'Follow specific policy issues for notifications' },
  notifications: { title: 'Notifications', subtitle: 'Bills matching topics you follow' },
}

// Stable, user-facing manual-entry copy for every detection failure. Raw
// ApiError.message, browser exception text, and response bodies are never shown.
const DETECT_ERROR = {
  insecure: 'Location detection needs a secure (HTTPS) connection. Please enter your address manually below.',
  unsupported: "Location detection isn't supported by your browser. Please enter your address manually below.",
  notFound: "No address match found for your location. Please enter your address manually below.",
  throttled: 'Too many location lookups right now. Please enter your address manually below.',
  providerDown: 'Address lookup is temporarily unavailable. Please enter your address manually below.',
  unconfigured: "Location address lookup isn't available right now. Please enter your address manually below.",
  timeout: 'Address lookup took too long. Please enter your address manually below.',
  generic: "Couldn't detect your location. Please enter your address manually below.",
} as const

// Required review-and-save copy shown after a successful prefill.
const PREFILL_REVIEW_MESSAGE = 'Filled from your location. Review and save to update districts. Please confirm this is your home address and check the apartment or unit line.'

function metaForPath(pathname: string): TabMeta {
  if (pathname === '/' || pathname === '') return TAB_META.dashboard
  if (/^\/bills\/[^/]+\/roll-call\//.test(pathname)) return { title: 'Roll Call', subtitle: 'Member-by-member recorded votes' }
  if (pathname.startsWith('/bills/')) return { title: 'Bill Details', subtitle: 'Full bill information' }
  if (pathname === '/bills') return TAB_META.bills
  if (pathname.startsWith('/representatives/')) return { title: 'Representative Profile', subtitle: 'Official profile and voting record' }
  if (pathname === '/representatives') {
    return { title: 'Representatives', subtitle: 'Browse officials at each level and open profile details' }
  }
  if (pathname.startsWith('/actions/')) return { title: 'Call to Action', subtitle: 'Review and take action' }
  // Profile/Settings use the same parent title; the per-section sidebar nav
  // inside the view handles the user's sense of "where am I". Keep the TopBar
  // title stable so we don't duplicate headings between TopBar and section.
  if (pathname.startsWith('/profile')) return TAB_META.profile
  if (pathname.startsWith('/settings')) return TAB_META.settings
  const key = pathname.replace(/^\//, '').split('/')[0]
  return TAB_META[key] ?? TAB_META.dashboard
}

export default function App() {
  const location = useLocation()
  const navigate = useNavigate()
  const navigationType = useNavigationType()
  const mainRef = useRef<HTMLElement | null>(null)
  const previousPathnameRef = useRef<string | null>(null)
  const authGenerationRef = useRef(0)
  const sessionRef = useRef<ApiSession | undefined>(undefined)
  const provenanceRef = useRef(createRouteProvenanceSession())
  const entitlementRefreshEntryRef = useRef<string | null>(null)
  const questionnaireSignalSeenRef = useRef(0)
  const notificationMutationEpochRef = useRef(0)
  const notificationSummaryRequestRef = useRef(0)
  // Address-detection ownership: one monotonic epoch, one HTTP AbortController,
  // and one synchronous in-flight guard shared by the geolocation and prefill
  // phases so same-tick double taps cannot start duplicate paid lookups.
  const addressDetectEpochRef = useRef(0)
  const addressDetectControllerRef = useRef<AbortController | null>(null)
  const addressDetectInFlightRef = useRef(false)
  // Address fields the user explicitly edited (edits AND explicit clears) in
  // this draft. Set only by user changes — navigation and detection epochs
  // never mark fields — so a late settings load can hydrate every untouched
  // field without clobbering user values.
  const addressDirtyFieldsRef = useRef<Set<keyof UserAddress>>(new Set())

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null)
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null)
  const [districtContext, setDistrictContext] = useState<DistrictContextResponse['context'] | null>(null)
  const [addressForm, setAddressForm] = useState<UserAddress>(DEFAULT_ADDRESS)
  const [congressApiKeyInput, setCongressApiKeyInput] = useState('')
  const [civicSettingsMessage, setCivicSettingsMessage] = useState<string | null>(null)
  const [settingsBusy, setSettingsBusy] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [showAddressEditor, setShowAddressEditor] = useState(true)
  const [geolocationBusy, setGeolocationBusy] = useState(false)
  const [geolocationError, setGeolocationError] = useState<string | null>(null)
  const [geolocationWarning, setGeolocationWarning] = useState<string | null>(null)
  const [geolocationSuccess, setGeolocationSuccess] = useState<string | null>(null)
  const [civicCounts, setCivicCounts] = useState<{
    bills: number
    elections: number
  } | null>(null)
  const [civicBusy, setCivicBusy] = useState(false)
  const [civicError, setCivicError] = useState<string | null>(null)
  const [civicSyncStatus, setCivicSyncStatus] = useState<CivicSyncStatus | null>(null)
  const [civicSyncRuns, setCivicSyncRuns] = useState<CivicSyncRun[]>([])
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true)
  const [profileNameInput, setProfileNameInput] = useState('')
  const [profileBusy, setProfileBusy] = useState(false)
  const [profileMessage, setProfileMessage] = useState<string | null>(null)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [questionnaireDefinition, setQuestionnaireDefinition] = useState<QuestionnaireDefinition | null>(null)
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<Record<string, number | null>>({})
  const [questionnaireDirtyIds, setQuestionnaireDirtyIds] = useState<Set<string>>(new Set())
  const [questionnaireSkippedIds, setQuestionnaireSkippedIds] = useState<Set<string>>(new Set())
  const [questionnaireBaseline, setQuestionnaireBaseline] = useState<QuestionnaireResponses | null>(null)
  const [questionnaireResult, setQuestionnaireResult] = useState<QuestionnaireResponseResult | null>(null)
  const [questionnaireBusy, setQuestionnaireBusy] = useState(false)
  const [questionnaireError, setQuestionnaireError] = useState<string | null>(null)
  const [questionnaireRecoveryUnavailable, setQuestionnaireRecoveryUnavailable] = useState(false)
  const [isEditingQuestionnaire, setIsEditingQuestionnaire] = useState(false)
  const [questionnaireExternalConflict, setQuestionnaireExternalConflict] = useState<string | null>(null)
  const [accountBusy, setAccountBusy] = useState(false)
  const [accountError, setAccountError] = useState<string | null>(null)
  // Whether the current account has a Constituent password. Defaults to true
  // so older backends that don't return has_password still show the control.
  const [hasPassword, setHasPassword] = useState(true)
  const [entitlement, setEntitlement] = useState<{ value: Entitlement | null; asOf: number; status: 'fresh' | 'stale' | 'unavailable' } | null>(null)

  // LLM / Analysis state
  const [llmSettings, setLlmSettings] = useState<LLMSettings | null>(null)
  const [llmProviderInput, setLlmProviderInput] = useState('')
  const [llmEndpointInput, setLlmEndpointInput] = useState('')
  const [llmModelInput, setLlmModelInput] = useState('')
  const [llmApiKeyInput, setLlmApiKeyInput] = useState('')
  const [llmBusy, setLlmBusy] = useState(false)
  const [llmMessage, setLlmMessage] = useState<string | null>(null)
  const [llmError, setLlmError] = useState<string | null>(null)
  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus | null>(null)

  // CTA state
  const [ctaSummary, setCtaSummary] = useState<CTASummary | null>(null)

  // Notification badge state
  const [notificationSummary, setNotificationSummary] = useState<NotificationsSummary | null>(null)

  const [themeResolution, setThemeResolution] = useState<ThemeResolution>(
    () => resolveThemePreference(),
  )

  const handleThemeChange = useCallback((newTheme: ThemePreference) => {
    if (newTheme === 'system') {
      saveThemePreference('system')
      const next = resolveThemePreference()
      setThemeResolution(next)
      return
    }
    setThemeResolution({ theme: newTheme, followsSystem: false })
    saveThemePreference(newTheme)
  }, [])

  // Auth state for multi-user mode
  const [authChecked, setAuthChecked] = useState(false)
  const [needsLogin, setNeedsLogin] = useState(false)
  const [bootError, setBootError] = useState<string | null>(null)
  const [bootRetry, setBootRetry] = useState(0)

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------
  const addressKnown = userSettings?.lookup_status === 'resolved' && hasAddressConfigured(userSettings)
  const sidebarDistrictSummary = getDistrictSummary(userSettings)
  const currentMeta = location.pathname === '/profile/values/questionnaire'
    ? { title: 'Values questionnaire', subtitle: 'Review and update your values' }
    : location.pathname.startsWith('/bills/') && new URLSearchParams(location.search).get('reader') === 'text'
      ? { title: 'Bill text', subtitle: 'Focused bill text reader' }
      : metaForPath(location.pathname)
  const shell = getShellPresentation(location.pathname, location.search)
  const shellOwnerKey = shell.ownerKey ? `${shell.ownerKey}::${location.key}` : null

  // Single invalidation point for address detection: bumps the epoch (so every
  // in-flight callback and post-await update loses ownership), aborts the
  // HTTP phase, drops the in-flight owner, and immediately clears busy and the
  // approximate-location warning (a stale warning must not survive
  // cancellation or manual edits). Invoked on a newer detection, manual
  // address edit, Save, Cancel, location-route departure/replacement, and
  // private-session reset.
  const invalidateAddressDetection = useCallback(() => {
    addressDetectEpochRef.current += 1
    addressDetectControllerRef.current?.abort()
    addressDetectControllerRef.current = null
    addressDetectInFlightRef.current = false
    setGeolocationBusy(false)
    setGeolocationWarning(null)
  }, [])

  const clearPrivateState = useCallback(() => {
    setUserSettings(null)
    setDistrictContext(null)
    setQuestionnaireDefinition(null)
    setQuestionnaireAnswers({})
    setQuestionnaireDirtyIds(new Set())
    setQuestionnaireSkippedIds(new Set())
    setQuestionnaireBaseline(null)
    setQuestionnaireResult(null)
    setCtaSummary(null)
    setNotificationSummary(null)
    notificationMutationEpochRef.current += 1
    notificationSummaryRequestRef.current += 1
    setEntitlement(null)
    setProfileBusy(false)
    setProfileMessage(null)
    setProfileError(null)
    setQuestionnaireBusy(false)
    setQuestionnaireError(null)
    setQuestionnaireRecoveryUnavailable(false)
    setQuestionnaireExternalConflict(null)
    setAccountBusy(false)
    setAccountError(null)
    setSettingsBusy(false)
    setSettingsError(null)
    setCivicError(null)
    // Address drafts, geolocation banners, dirty-field tracking, and active
    // detection ownership must not survive an account switch — a prior
    // candidate would leak across sessions otherwise.
    invalidateAddressDetection()
    addressDirtyFieldsRef.current = new Set()
    setAddressForm(DEFAULT_ADDRESS)
    setGeolocationError(null)
    setGeolocationWarning(null)
    setGeolocationSuccess(null)
    clearPrivateSessionStorage()
    clearBillListReturnMemory()
    provenanceRef.current.clear()
    questionnaireSignalSeenRef.current = 0
  }, [invalidateAddressDetection])

  const makeSession = useCallback((expectedToken: string, userId: string): ApiSession => ({
    expectedToken,
    generation: authGenerationRef.current,
    userId,
    mode: 'multi',
    isCurrent: (session) => {
      const current = sessionRef.current
      return Boolean(current
        && current.expectedToken === session.expectedToken
        && current.generation === session.generation
        && current.userId === session.userId
        && (getToken() ?? '') === session.expectedToken)
    },
  }), [])

  const currentSession = useCallback(() => sessionRef.current, [])
  // Deliberately captured during render. Event handlers from an old private
  // tree retain the old owner instead of consulting the latest global token.
  const renderedSession = sessionRef.current
  const renderedGeneration = authGenerationRef.current
  const questionnaireOwnerIsCurrent = useCallback((session?: ApiSession) => {
    if (authGenerationRef.current !== renderedGeneration) return false
    if (session) return session.isCurrent(session)
    return systemInfo?.mode !== 'multi' && sessionRef.current === undefined
  }, [renderedGeneration, systemInfo?.mode])

  const notificationSessionIsCurrent = useCallback((session?: ApiSession) => {
    if (session) return session.isCurrent(session)
    return systemInfo?.mode !== 'multi'
  }, [systemInfo?.mode])

  const invalidateNotificationMutation = useCallback((session?: ApiSession) => {
    if (!notificationSessionIsCurrent(session)) return
    notificationMutationEpochRef.current += 1
    notificationSummaryRequestRef.current += 1
    setNotificationSummary(null)
  }, [notificationSessionIsCurrent])

  const requestNotificationSummary = useCallback(async (signal?: AbortSignal, session = currentSession()) => {
    if (!notificationSessionIsCurrent(session)) return null
    const requestStamp = ++notificationSummaryRequestRef.current
    const mutationEpoch = notificationMutationEpochRef.current
    try {
      const data = await getNotificationsSummary(signal, session)
      if (signal?.aborted
        || requestStamp !== notificationSummaryRequestRef.current
        || mutationEpoch !== notificationMutationEpochRef.current
        || !notificationSessionIsCurrent(session)) return null
      setNotificationSummary(data)
      return data
    } catch (error) {
      if (!signal?.aborted
        && requestStamp === notificationSummaryRequestRef.current
        && mutationEpoch === notificationMutationEpochRef.current
        && notificationSessionIsCurrent(session)) {
        setNotificationSummary(null)
        console.error('loadNotificationSummary failed:', error)
      }
      return null
    }
  }, [currentSession, notificationSessionIsCurrent])

  const coordinatorInvalidateNotificationMutation = useCallback(
    () => invalidateNotificationMutation(renderedSession),
    [invalidateNotificationMutation, renderedSession],
  )
  const coordinatorRefreshNotificationSummary = useCallback(
    (signal?: AbortSignal) => requestNotificationSummary(signal, renderedSession),
    [renderedSession, requestNotificationSummary],
  )
  const notificationCoordinator = useMemo<NotificationSummaryCoordinator>(() => ({
    summary: notificationSummary,
    invalidateMutation: coordinatorInvalidateNotificationMutation,
    refresh: coordinatorRefreshNotificationSummary,
  }), [coordinatorInvalidateNotificationMutation, coordinatorRefreshNotificationSummary, notificationSummary])

  useEffect(() => {
    document.title = authChecked && !needsLogin ? `${currentMeta.title} · Constituent` : 'Constituent'
  }, [authChecked, needsLogin, currentMeta.title])

  // ---------------------------------------------------------------------------
  // Data loaders
  // ---------------------------------------------------------------------------
  const loadSettings = async (signal?: AbortSignal) => {
    const session = currentSession()
    try {
      const data = await getSettings(signal, session)
      if (session && !session.isCurrent(session)) return
      if (!data?.settings) {
        setSettingsError(null)
        return
      }
      setUserSettings(data.settings)
      setProfileNameInput(data.settings.display_name || 'John Doe')
      // Per-field hydration at arrival: fields the user explicitly edited
      // (edits and explicit clears) keep their draft value; untouched fields
      // hydrate from the server. The editor only auto-collapses while the
      // user has no in-progress draft edits.
      const incoming = data.settings.address
      const dirty = addressDirtyFieldsRef.current
      if (dirty.size === 0) {
        setAddressForm(incoming)
        const resolved = data.settings.lookup_status === 'resolved' && hasAddressConfigured(data.settings)
        setShowAddressEditor(!resolved)
      } else {
        setAddressForm((prev) => ({
          address_line1: dirty.has('address_line1') ? prev.address_line1 : incoming.address_line1,
          address_line2: dirty.has('address_line2') ? prev.address_line2 : incoming.address_line2,
          city: dirty.has('city') ? prev.city : incoming.city,
          state: dirty.has('state') ? prev.state : incoming.state,
          zip: dirty.has('zip') ? prev.zip : incoming.zip,
        }))
      }
      setAutoSyncEnabled(Boolean(data.settings.civic_auto_sync_enabled))
      setSettingsError(null)
    } catch (error) {
      if (!signal?.aborted && (!session || session.isCurrent(session))) setSettingsError(error instanceof Error ? error.message : 'Failed to load settings')
    }
  }

  const loadDistrictContext = async (signal?: AbortSignal) => {
    const session = currentSession()
    try {
      const data = await getDistrictContext(signal, session)
      if (session && !session.isCurrent(session)) return
      setDistrictContext(data.context)
    } catch (error) {
      if (!signal?.aborted && (!session || session.isCurrent(session))) { console.error('loadDistrictContext failed:', error); setDistrictContext(null) }
    }
  }

  const loadQuestionnaire = async (signal?: AbortSignal) => {
    const session = currentSession()
    try {
      const [definitionRes, responsesRes] = await Promise.all([
        getQuestionnaireDefinition(signal, session),
        getQuestionnaireResponses(signal, session),
      ])
      if (session && !session.isCurrent(session)) return
      const definition = definitionRes.questionnaire
      const responseAnswers = responsesRes.responses.answers || {}
      const initialAnswers: Record<string, number | null> = {}
      definition.questions.forEach((question) => {
        const value = responseAnswers[question.id]
        initialAnswers[question.id] = typeof value === 'number' ? value : null
      })
      setQuestionnaireDefinition(definition)
      setQuestionnaireAnswers(initialAnswers)
      setQuestionnaireDirtyIds(new Set())
      setQuestionnaireSkippedIds(new Set())
      setQuestionnaireResult(responsesRes.responses.result)
      setQuestionnaireBaseline(responsesRes.responses)
      setQuestionnaireExternalConflict(null)
      setQuestionnaireError(null)
      setQuestionnaireRecoveryUnavailable(false)
    } catch (error) {
      if (!signal?.aborted && (!session || session.isCurrent(session))) setQuestionnaireError(error instanceof Error ? error.message : 'Failed to load questionnaire')
    }
  }

  const loadCivicFeed = async (signal?: AbortSignal) => {
    const session = currentSession()
    try {
      const data = await getCivicFeedContext(signal, session)
      if (session && !session.isCurrent(session)) return
      setCivicCounts(data.context.counts)
      if (data.context.sync_status) {
        setCivicSyncStatus(data.context.sync_status)
      }
      setCivicError(null)
    } catch (error) {
      if (!signal?.aborted && (!session || session.isCurrent(session))) setCivicError(error instanceof Error ? error.message : 'Failed to load civic feed')
    }
  }

  const loadCivicActivity = async (signal?: AbortSignal) => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        getCivicSyncStatus(signal),
        getCivicSyncHistory(8, signal),
      ])
      setCivicSyncStatus(statusRes.status)
      setCivicSyncRuns(historyRes.runs)
    } catch (error) {
      if (!signal?.aborted) setCivicError(error instanceof Error ? error.message : 'Failed to load civic activity')
    }
  }

  const loadLLMSettings = async (signal?: AbortSignal) => {
    try {
      const data = await getLLMSettings(signal)
      setLlmSettings(data)
      setLlmProviderInput(data.provider)
      setLlmEndpointInput(data.endpoint_url)
      setLlmModelInput(data.model)
      setLlmApiKeyInput('')
    } catch (error) { if (!signal?.aborted) console.error('loadLLMSettings failed:', error) }
  }

  const loadAnalysisStatusData = async (signal?: AbortSignal) => {
    try {
      const data = await getAnalysisStatus(signal)
      setAnalysisStatus(data.analysis)
    } catch (error) { if (!signal?.aborted) console.error('loadAnalysisStatusData failed:', error) }
  }

  const loadCTASummary = async (signal?: AbortSignal) => {
    const session = currentSession()
    try {
      const data = await getCTASummary(signal, session)
      if (session && !session.isCurrent(session)) return
      setCtaSummary(data)
    } catch (error) { if (!signal?.aborted && (!session || session.isCurrent(session))) console.error('loadCTASummary failed:', error) }
  }

  const refreshCTASummaryAfterMutation = useCallback(async () => {
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    try {
      const data = await getCTASummary(undefined, session)
      if (session && !session.isCurrent(session)) return
      setCtaSummary(data)
    } catch {
      // The acknowledged CTA state remains valid when ancillary summary
      // reconciliation fails. A later explicit load can refresh the badge.
    }
  }, [renderedSession])

  const loadNotificationSummary = async (signal?: AbortSignal) => {
    await requestNotificationSummary(signal, currentSession())
  }

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  useLayoutEffect(() => {
    applyTheme(themeResolution.theme)
  }, [themeResolution.theme])

  useEffect(() => {
    if (!themeResolution.followsSystem) return
    return watchSystemTheme((theme) => {
      setThemeResolution((current) => {
        if (!current.followsSystem || current.theme === theme) return current
        return { theme, followsSystem: true }
      })
    })
  }, [themeResolution.followsSystem])

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller
    setAuthChecked(false)
    setNeedsLogin(false)
    setBootError(null)
    setActiveApiSession(undefined)
    const loadAll = (isMulti: boolean) => {
      void loadSettings(signal)
      void loadQuestionnaire(signal)
      void loadDistrictContext(signal)
      void loadCivicFeed(signal)
      void loadLLMSettings(signal)
      void loadCTASummary(signal)
      void loadNotificationSummary(signal)
      // Sync/analysis status is operator-facing UI that multi-user mode never
      // renders, so skip those fetches entirely there.
      if (!isMulti) {
        void loadCivicActivity(signal)
        void loadAnalysisStatusData(signal)
      }
    }

    const wait = (ms: number) => new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(resolve, ms)
      signal.addEventListener('abort', () => { window.clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')) }, { once: true })
    })
    const run = async () => {
      try {
        const info = await getSystemInfo(signal)
        if (signal.aborted) return
        setSystemInfo(info)
        if (info.mode !== 'multi') {
          sessionRef.current = undefined
          setActiveApiSession(null)
          loadAll(false)
          setAuthChecked(true)
          return
        }
        setUnauthorizedHandler(() => {
          authGenerationRef.current += 1
          sessionRef.current = undefined
          setActiveApiSession(undefined)
          controller.abort()
          clearPrivateState()
          setNeedsLogin(true)
          setAuthChecked(true)
        })
        if (!isAuthenticated()) {
          sessionRef.current = undefined
          setActiveApiSession(undefined)
          controller.abort()
          clearPrivateState()
          setNeedsLogin(true)
          setAuthChecked(true)
          return
        }
        const expectedToken = getToken() ?? ''
        let me
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            me = await authMe(signal, null)
            break
          } catch (error) {
            if (signal.aborted) return
            if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
              const currentToken = getToken()
              if (currentToken && currentToken !== expectedToken) {
                authGenerationRef.current += 1
                sessionRef.current = undefined
                setActiveApiSession(undefined)
                clearPrivateState()
                controller.abort()
                setAuthChecked(false)
                setNeedsLogin(false)
                setBootRetry((value) => value + 1)
                return
              }
              if (error.status !== 401 && (currentToken ?? '') === expectedToken) clearToken()
              controller.abort()
              setNeedsLogin(true)
              setAuthChecked(true)
              return
            }
            if (attempt === 2) throw error
            await wait(100 * (attempt + 1))
          }
        }
        if (!me || signal.aborted) return
        if ((getToken() ?? '') !== expectedToken) {
          authGenerationRef.current += 1
          sessionRef.current = undefined
          setActiveApiSession(undefined)
          clearPrivateState()
          if (getToken()) {
            setNeedsLogin(false)
            setAuthChecked(false)
            setBootRetry((value) => value + 1)
          } else {
            setNeedsLogin(true)
            setAuthChecked(true)
          }
          return
        }
        sessionRef.current = makeSession(expectedToken, me.user.id)
        setActiveApiSession(sessionRef.current)
        const priorQuestionnaireSignal = readQuestionnaireSubmitInvalidation()
        questionnaireSignalSeenRef.current = priorQuestionnaireSignal?.userId === me.user.id ? priorQuestionnaireSignal.at : 0
        setEntitlement({ value: me.entitlement ?? null, asOf: Date.now(), status: me.entitlement ? 'fresh' : 'unavailable' })
        if (typeof me.user.has_password === 'boolean') setHasPassword(me.user.has_password)
        loadAll(true)
        setAuthChecked(true)
      } catch (error) {
        if (signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return
        controller.abort()
        setBootError(error instanceof Error ? error.message : "Couldn't start the app")
      }
    }
    void run()
    return () => {
      controller.abort()
      setUnauthorizedHandler(null)
    }
  }, [bootRetry, clearPrivateState, makeSession])

  useEffect(() => {
    const stopAuth = onAuthChange(({ token }) => {
      const current = sessionRef.current
      if (current?.expectedToken === token) return
      authGenerationRef.current += 1
      sessionRef.current = undefined
      setActiveApiSession(undefined)
      clearPrivateState()
      if (token) {
        setNeedsLogin(false)
        setAuthChecked(false)
        setBootRetry((value) => value + 1)
      } else {
        setNeedsLogin(true)
        setAuthChecked(true)
      }
    })
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== 'openvoter_token') return
      const current = sessionRef.current
      if (current?.expectedToken === event.newValue) return
      authGenerationRef.current += 1
      sessionRef.current = undefined
      setActiveApiSession(undefined)
      clearPrivateState()
      if (event.newValue) {
        setNeedsLogin(false)
        setAuthChecked(false)
        setBootRetry((value) => value + 1)
      } else {
        setNeedsLogin(true)
        setAuthChecked(true)
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => { stopAuth(); window.removeEventListener('storage', handleStorage) }
  }, [clearPrivateState])

  useEffect(() => {
    if (!renderedSession || !questionnaireDefinition) return
    const reconcile = () => {
      const signal = readQuestionnaireSubmitInvalidation()
      if (!signal || signal.at <= questionnaireSignalSeenRef.current || signal.userId !== renderedSession.userId) return
      questionnaireSignalSeenRef.current = signal.at
      const storage = availableSessionStorage()
      const savedAt = questionnaireDraftSavedAt(storage, renderedSession.userId, questionnaireDefinition.version)
      const hasLocalEdits = questionnaireDirtyIds.size > 0 || (savedAt !== null && savedAt > signal.at)
      if (signal.definitionVersion !== questionnaireDefinition.version || hasLocalEdits) {
        setQuestionnaireExternalConflict(signal.definitionVersion === questionnaireDefinition.version
          ? 'Your questionnaire was saved in another tab while this tab has local edits. Choose which state to keep.'
          : 'Another tab saved a different questionnaire version. Restart from the server before submitting this draft.')
        return
      }
      removeQuestionnaireDraftAtOrBefore(storage, renderedSession.userId, questionnaireDefinition.version, signal.at)
      void loadQuestionnaire()
    }
    const onStorage = (event: StorageEvent) => { if (event.key === 'constituent.questionnaire.submit.v1') reconcile() }
    const onVisibility = () => { if (document.visibilityState === 'visible') reconcile() }
    window.addEventListener('storage', onStorage)
    window.addEventListener('focus', reconcile)
    document.addEventListener('visibilitychange', onVisibility)
    reconcile()
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('focus', reconcile)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [questionnaireDefinition, questionnaireDirtyIds, renderedSession])

  // Background polling. Gated on the auth check so the login screen fires no
  // requests; multi-user mode polls notifications only — sync/analysis status
  // is single-user (desktop) UI.
  useEffect(() => {
    if (!authChecked || needsLogin) return
    const isMulti = systemInfo?.mode === 'multi'
    const timers: number[] = []
    if (!isMulti) {
      // 10s cadence: drives the live sync-progress card on the dashboard
      timers.push(window.setInterval(() => {
        void loadCivicActivity()
        void loadAnalysisStatusData()
      }, 10000))
    }
    timers.push(window.setInterval(() => {
      void loadNotificationSummary()
    }, 60000))
    return () => timers.forEach((timer) => window.clearInterval(timer))
  }, [authChecked, needsLogin, systemInfo])

  // Close mobile sidebar on route change, move focus to main content so
  // screen readers announce the new page, and scroll to top so a new route
  // doesn't inherit the previous page's scroll position.
  useEffect(() => {
    setSidebarOpen(false)
    const previousPathname = previousPathnameRef.current
    previousPathnameRef.current = location.pathname
    if (previousPathname === location.pathname) return
    if (location.hash) return
    if (navigationType === 'POP' && billListReturnIsPending(location.key, location.pathname, location.search)) return
    if (shell.focused) { window.scrollTo(0, 0); return }
    mainRef.current?.focus({ preventScroll: true })
    window.scrollTo(0, 0)
  }, [location.hash, location.key, location.pathname, location.search, navigationType, shell.focused])

  useLayoutEffect(() => {
    if (!authChecked || needsLogin) return
    provenanceRef.current.observe({ key: location.key, pathname: location.pathname, search: location.search, hash: location.hash }, navigationType)
  }, [authChecked, location.hash, location.key, location.pathname, location.search, navigationType, needsLogin])

  useEffect(() => {
    if (needsLogin) clearBillListReturnMemory()
  }, [needsLogin])

  // Force-close the mobile drawer when the viewport crosses into the
  // large-screen breakpoint — otherwise dialog semantics would be left on
  // while the sidebar is CSS-persistent.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const handle = (event: MediaQueryListEvent) => {
      if (event.matches) setSidebarOpen(false)
    }
    mq.addEventListener('change', handle)
    return () => mq.removeEventListener('change', handle)
  }, [])

  // Lazy-load questionnaire when landing on profile route.
  useEffect(() => {
    if (!authChecked || needsLogin || location.pathname !== '/profile') return
    if (questionnaireDefinition) return
    void loadQuestionnaire()
  }, [authChecked, needsLogin, location.pathname, questionnaireDefinition])

  useEffect(() => {
    if (!authChecked || needsLogin || location.pathname !== '/profile/security') {
      entitlementRefreshEntryRef.current = null
      return
    }
    const current = entitlement
    const expiresAt = current?.value?.expires_at ? Date.parse(current.value.expires_at) : null
    const shouldRefresh = !current || current.status !== 'fresh' || Date.now() - current.asOf > 5 * 60_000 || (expiresAt !== null && expiresAt <= Date.now() + 5 * 60_000)
    if (!shouldRefresh) return
    const session = currentSession()
    const entryKey = `${session?.generation ?? 'single'}:${location.key}`
    if (entitlementRefreshEntryRef.current === entryKey) return
    entitlementRefreshEntryRef.current = entryKey
    const controller = new AbortController()
    authMe(controller.signal, session ?? null).then((me) => {
      if (controller.signal.aborted || (session && !session.isCurrent(session))) return
      setEntitlement({ value: me.entitlement ?? null, asOf: Date.now(), status: me.entitlement ? 'fresh' : 'unavailable' })
    }).catch(() => {
      if (controller.signal.aborted || (session && !session.isCurrent(session))) return
      setEntitlement((prev) => prev ? { ...prev, status: prev.value?.expires_at && Date.parse(prev.value.expires_at) <= Date.now() ? 'unavailable' : 'stale' } : { value: null, asOf: Date.now(), status: 'unavailable' })
    })
    return () => controller.abort()
  }, [authChecked, currentSession, entitlement, location.key, location.pathname, needsLogin])

  useEffect(() => {
    const expiresAt = entitlement?.value?.expires_at ? Date.parse(entitlement.value.expires_at) : null
    if (!expiresAt || entitlement?.value?.active !== true || entitlement.status !== 'fresh') return undefined
    let timer: number | undefined
    const schedule = () => {
      const remaining = expiresAt - Date.now()
      if (remaining <= 0) {
        setEntitlement((prev) => prev ? { ...prev, status: 'stale' } : prev)
        return
      }
      // Browsers clamp larger delays to a signed 32-bit integer, often firing
      // immediately. Rearm only when that maximum one-shot interval elapses.
      timer = window.setTimeout(schedule, Math.min(remaining, 2_147_483_647))
    }
    schedule()
    return () => { if (timer !== undefined) window.clearTimeout(timer) }
  }, [entitlement])

  // App stays mounted across route changes, so address-detection ownership is
  // dropped when the /profile/location route instance goes away: the cleanup
  // runs on leave (any destination) and on same-path location-key replacement,
  // and never fires for unrelated routes. No invalidation on entry.
  useEffect(() => {
    if (location.pathname !== '/profile/location') return undefined
    return () => invalidateAddressDetection()
  }, [invalidateAddressDetection, location.key, location.pathname])

  const handleLoginSuccess = useCallback(() => {
    window.location.reload()
  }, [])

  const handleLogout = useCallback(() => {
    // Local privacy state is authoritative for an explicit sign-out. Do not
    // leave a private route or title visible while the server hook is pending.
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    const logoutRequest = logout(session)
    clearBillListReturnMemory()
    authGenerationRef.current += 1
    sessionRef.current = undefined
    setActiveApiSession(undefined)
    clearPrivateState()
    clearToken()
    document.title = 'Constituent'
    setNeedsLogin(true)
    void logoutRequest.catch(() => {
      // The API implementation is already fail-local, but keep this boundary
      // rejection-safe for alternate clients and tests.
    })
  }, [clearPrivateState, renderedSession])

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleAddressChange = (field: keyof UserAddress, value: string) => {
    // Manual edits always win over a detection that has not landed yet, and
    // mark the field so a later settings hydration preserves this value
    // (including explicit clears). Navigation/detection epochs never mark.
    invalidateAddressDetection()
    addressDirtyFieldsRef.current.add(field)
    setAddressForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSaveAddress = async () => {
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    invalidateAddressDetection()
    try {
      setSettingsBusy(true); setSettingsError(null); setCivicSettingsMessage(null)
      const payload: UserAddress = { ...addressForm, state: addressForm.state.toUpperCase() }
      const data = await saveAddress(payload, session)
      if (session && !session.isCurrent(session)) return
      addressDirtyFieldsRef.current = new Set()
      setUserSettings(data.settings); setAddressForm(data.settings.address)
      if (data.settings.lookup_status === 'resolved' && hasAddressConfigured(data.settings)) setShowAddressEditor(false)
      setGeolocationError(null); setGeolocationWarning(null); setGeolocationSuccess(null)
      void loadDistrictContext()
    } catch (error) { if (!session || session.isCurrent(session)) setSettingsError(error instanceof Error ? error.message : 'Failed to save address and resolve districts') }
    finally { if (!session || session.isCurrent(session)) setSettingsBusy(false) }
  }

  // Explicit Cancel: restore the persisted address, drop any candidate state,
  // and close the editor only when a persisted address exists.
  const handleCancelAddress = () => {
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    invalidateAddressDetection()
    addressDirtyFieldsRef.current = new Set()
    const persisted = userSettings?.address
    if (persisted) setAddressForm({ ...persisted })
    setGeolocationError(null)
    setGeolocationWarning(null)
    setGeolocationSuccess(null)
    if (hasAddressConfigured(userSettings)) setShowAddressEditor(false)
  }

  const handleDetectLocation = () => {
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    // HTTPS (or a browser-trusted localhost context) is required; the earlier
    // LAN-IP HTTP limitation is not solved by reverse geocoding.
    if (!window.isSecureContext) {
      setGeolocationError(DETECT_ERROR.insecure)
      return
    }
    if (!navigator.geolocation) {
      setGeolocationError(DETECT_ERROR.unsupported)
      return
    }
    // Synchronous in-flight guard, checked and set before getCurrentPosition so
    // same-tick double taps cannot start duplicate paid lookups.
    if (addressDetectInFlightRef.current) return
    // A newer detection supersedes the previous one.
    invalidateAddressDetection()
    const controller = new AbortController()
    addressDetectControllerRef.current = controller
    const requestEpoch = ++addressDetectEpochRef.current
    addressDetectInFlightRef.current = true
    setGeolocationBusy(true)
    setGeolocationError(null)
    setGeolocationWarning(null)
    setGeolocationSuccess(null)
    // Balanced-power analogue of the native request: no high accuracy, a
    // bounded timeout, and never a cached position.
    try {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          if (requestEpoch !== addressDetectEpochRef.current) return
          if (session && !session.isCurrent(session)) return
          const { latitude, longitude, accuracy } = position.coords
          if (accuracy > 500) {
            setGeolocationWarning(`Your location is approximate (~${Math.round(accuracy)}m). Please verify the address below after detection.`)
          }
          try {
            const data = await prefillAddressByCoordinates(latitude, longitude, session, controller.signal)
            if (requestEpoch !== addressDetectEpochRef.current) return
            if (session && !session.isCurrent(session)) return
            // Prefill only: update the draft, keeping the current apartment/unit
            // line so detection cannot silently drop a saved unit.
            setAddressForm((prev) => ({ ...data.address, address_line2: prev.address_line2 }))
            setGeolocationSuccess(PREFILL_REVIEW_MESSAGE)
          } catch (error) {
            if (requestEpoch !== addressDetectEpochRef.current) return
            if (session && !session.isCurrent(session)) return
            // Name-based check: the AbortError thrown by an aborted fetch is
            // realm-specific in some environments, so instanceof DOMException
            // is not reliable here. With the epoch still owned, an AbortError
            // can only be the 15-second HTTP deadline (invalidation bumps the
            // epoch before aborting).
            if ((error as { name?: string })?.name === 'AbortError') {
              setGeolocationError(DETECT_ERROR.timeout)
              return
            }
            // Stable manual-entry copy only — never raw ApiError.message or
            // response bodies.
            setGeolocationError(error instanceof ApiError
              ? (
                error.status === 404 ? DETECT_ERROR.notFound
                  : error.status === 429 ? DETECT_ERROR.throttled
                    : error.status === 502 ? DETECT_ERROR.providerDown
                      : error.status === 503 ? DETECT_ERROR.unconfigured
                        : DETECT_ERROR.generic
              )
              : DETECT_ERROR.generic)
          } finally {
            if (requestEpoch === addressDetectEpochRef.current) {
              addressDetectInFlightRef.current = false
              setGeolocationBusy(false)
            }
          }
        },
        (error) => {
          if (requestEpoch !== addressDetectEpochRef.current) return
          if (session && !session.isCurrent(session)) return
          addressDetectInFlightRef.current = false
          setGeolocationBusy(false)
          const reasons: Record<number, string> = {
            1: 'Location access denied',
            2: 'Location unavailable',
            3: 'Location request timed out',
          }
          const reason = reasons[error.code] ?? 'Unknown error'
          setGeolocationError(`Couldn't detect your location. ${reason}. Please enter your address manually below.`)
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 },
      )
    } catch {
      // Some embedded webviews throw synchronously from getCurrentPosition;
      // the callbacks above never run, so release ownership here.
      addressDetectControllerRef.current = null
      addressDetectInFlightRef.current = false
      setGeolocationBusy(false)
      setGeolocationError(DETECT_ERROR.unsupported)
    }
  }

  const handleSaveCongressApiKey = async () => {
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    try {
      setSettingsBusy(true); setSettingsError(null); setCivicSettingsMessage(null)
      const data = await saveCivicSettings({ congress_api_key: congressApiKeyInput }, session)
      if (session && !session.isCurrent(session)) return
      setUserSettings(data.settings); setCivicSettingsMessage('Congress API key saved.')
    } catch (error) { if (!session || session.isCurrent(session)) setSettingsError(error instanceof Error ? error.message : 'Failed to save civic settings') }
    finally { if (!session || session.isCurrent(session)) setSettingsBusy(false) }
  }

  const handleClearCongressApiKey = async () => {
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    try {
      setSettingsBusy(true); setSettingsError(null); setCivicSettingsMessage(null)
      const data = await saveCivicSettings({ congress_api_key: '' }, session)
      if (session && !session.isCurrent(session)) return
      setUserSettings(data.settings); setCongressApiKeyInput('')
      setCivicSettingsMessage('Congress API key cleared. Bill sync is disabled until a key is set.')
    } catch (error) { if (!session || session.isCurrent(session)) setSettingsError(error instanceof Error ? error.message : 'Failed to clear civic settings') }
    finally { if (!session || session.isCurrent(session)) setSettingsBusy(false) }
  }

  const openBillWithInAppOrigin = useCallback((billId: string) => {
    navigate(`/bills/${encodeURIComponent(billId)}`, {
      state: captureInAppBillOrigin(location, billId),
    })
  }, [location, navigate])

  const handleSyncCivic = useCallback(async (trigger: 'manual' | 'auto' = 'manual') => {
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    if (civicBusy) return
    try { setCivicBusy(true); setCivicError(null); await syncCivic(trigger, session); if (session && !session.isCurrent(session)) return; await loadCivicFeed(); await loadCivicActivity() }
    catch (error) { if (!session || session.isCurrent(session)) setCivicError(error instanceof Error ? error.message : `Failed to sync civic feed (${trigger})`) }
    finally { if (!session || session.isCurrent(session)) setCivicBusy(false) }
  }, [civicBusy, renderedSession])

  const handleToggleAutoSync = async (enabled: boolean) => {
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    try {
      setSettingsBusy(true); setSettingsError(null); setCivicSettingsMessage(null)
      const data = await saveCivicSettings({ civic_auto_sync_enabled: enabled }, session)
      if (session && !session.isCurrent(session)) return
      setUserSettings(data.settings)
      setAutoSyncEnabled(Boolean(data.settings.civic_auto_sync_enabled))
      setCivicSettingsMessage(enabled ? 'Background sync enabled.' : 'Background sync disabled.')
    } catch (error) { if (!session || session.isCurrent(session)) setSettingsError(error instanceof Error ? error.message : 'Failed to update sync setting') }
    finally { if (!session || session.isCurrent(session)) setSettingsBusy(false) }
  }

  const handleSaveLLMSettings = async () => {
    try {
      setLlmBusy(true); setLlmError(null); setLlmMessage(null)
      const data = await saveLLMSettings({
        provider: llmProviderInput,
        endpoint_url: llmEndpointInput,
        model: llmModelInput,
        api_key: llmApiKeyInput,
      })
      setLlmSettings(data)
      setLlmApiKeyInput('')
      setLlmMessage('LLM settings saved.')
    } catch (error) { setLlmError(error instanceof Error ? error.message : 'Failed to save LLM settings') }
    finally { setLlmBusy(false) }
  }

  const handleTestLLMConnection = async () => {
    try {
      setLlmBusy(true); setLlmError(null); setLlmMessage(null)
      const data = await testLLMConnection()
      if (data.success) setLlmMessage(`Connection successful: ${data.message}`)
      else setLlmError(`Connection failed: ${data.message}`)
    } catch (error) { setLlmError(error instanceof Error ? error.message : 'Failed to test LLM connection') }
    finally { setLlmBusy(false) }
  }

  const handleSaveProfileName = async () => {
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    try {
      setProfileBusy(true); setProfileError(null); setProfileMessage(null)
      const response = await updateProfile({ display_name: profileNameInput.trim() || 'John Doe' }, session)
      if (session && !session.isCurrent(session)) return
      setProfileNameInput(response.profile.display_name)
      setUserSettings((prev) => prev ? { ...prev, display_name: response.profile.display_name } : prev)
      setProfileMessage('Name updated.')
    } catch (error) { if (!session || session.isCurrent(session)) setProfileError(error instanceof Error ? error.message : 'Failed to update profile name') }
    finally { if (!session || session.isCurrent(session)) setProfileBusy(false) }
  }

  const handleQuestionAnswerChange = (questionId: string, value: number) => {
    if (!questionnaireDefinition?.questions.some((question) => question.id === questionId)) return
    const nextAnswers = { ...questionnaireAnswers, [questionId]: value }
    const nextDirty = new Set(questionnaireDirtyIds).add(questionId)
    const nextSkipped = new Set(questionnaireSkippedIds); nextSkipped.delete(questionId)
    setQuestionnaireAnswers(nextAnswers)
    setQuestionnaireDirtyIds(nextDirty)
    setQuestionnaireSkippedIds(nextSkipped)
    if (renderedSession && questionnaireBaseline && !writeQuestionnaireDraft(availableSessionStorage(), renderedSession.userId, {
      schemaVersion: 1,
      definitionVersion: questionnaireDefinition.version,
      answers: validQuestionnaireAnswers(questionnaireDefinition, nextAnswers),
      dirtyIds: [...nextDirty],
      skippedIds: [...nextSkipped],
      step: 'intro',
      questionId: null,
      savedAt: Date.now(),
      baselineFingerprint: questionnaireResponseFingerprint(questionnaireDefinition, questionnaireBaseline),
    })) setQuestionnaireRecoveryUnavailable(true)
  }

  const handleSubmitQuestionnaire = () => {
    navigate('/profile/values/questionnaire?step=review')
  }

  const handleResetQuestionnaire = () => {
    setQuestionnaireError(null)
    setIsEditingQuestionnaire(true)
  }

  const discardQuestionnaireEdits = useCallback(() => {
    if (questionnaireDefinition && questionnaireBaseline) {
      setQuestionnaireAnswers(validQuestionnaireAnswers(questionnaireDefinition, questionnaireBaseline.answers))
    }
    if (renderedSession && questionnaireDefinition) removeQuestionnaireDraft(availableSessionStorage(), renderedSession.userId, questionnaireDefinition.version)
    setIsEditingQuestionnaire(false)
    setQuestionnaireDirtyIds(new Set())
    setQuestionnaireSkippedIds(new Set())
    setQuestionnaireExternalConflict(null)
    setQuestionnaireError(null)
  }, [questionnaireBaseline, questionnaireDefinition, renderedSession])

  const resolveQuestionnaireExternalConflict = useCallback(() => {
    if (renderedSession && questionnaireDefinition) removeQuestionnaireDraft(availableSessionStorage(), renderedSession.userId, questionnaireDefinition.version)
    setQuestionnaireDirtyIds(new Set())
    setQuestionnaireSkippedIds(new Set())
    setQuestionnaireExternalConflict(null)
    void loadQuestionnaire()
  }, [questionnaireDefinition, renderedSession])

  const handleDeleteAccount = async () => {
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    try {
      setAccountBusy(true); setAccountError(null)
      await deleteAccount(session)
      if (session && !session.isCurrent(session)) return
      clearBillListReturnMemory()
      authGenerationRef.current += 1
      sessionRef.current = undefined
      setActiveApiSession(undefined)
      clearPrivateState()
      // Account is soft-deleted server-side; the JWT is now useless. Drop it
      // locally and reload — App boots back into LoginView via the multi-mode
      // auth gate (the `if (needsLogin)` render gate).
      clearToken()
      window.location.reload()
    } catch (error) {
      if (session && !session.isCurrent(session)) return
      setAccountError(error instanceof Error ? error.message : 'Failed to delete account')
      setAccountBusy(false)
    }
  }

  const handleChangePassword = async (currentPassword: string, newPassword: string) => {
    const session = renderedSession
    if (session && !session.isCurrent(session)) return
    setAccountBusy(true)
    setAccountError(null)
    try {
      await changePassword(currentPassword, newPassword, session)
      if (session && !session.isCurrent(session)) return
      clearBillListReturnMemory()
      authGenerationRef.current += 1
      sessionRef.current = undefined
      setActiveApiSession(undefined)
      clearPrivateState()
      // Server revoked every refresh family. Drop the (still-access-JWT-valid)
      // local token and bounce back to the auth gate. The user signs in again
      // with the new password.
      clearToken()
      setNeedsLogin(true)
      window.location.reload()
    } catch (error) {
      if (session && !session.isCurrent(session)) return
      setAccountBusy(false)
      setAccountError(error instanceof Error ? error.message : 'Failed to change password')
    }
  }

  // ---------------------------------------------------------------------------
  // Auth gate
  // ---------------------------------------------------------------------------
  if (location.pathname === '/auth/apple/complete') {
    return <AppleWebCompleteRoute />
  }

  if (bootError) {
    return (
      <div className="min-h-screen flex flex-col gap-3 items-center justify-center bg-slate-50">
        <p role="alert" className="text-slate-700">Couldn’t start the app.</p>
        <button type="button" onClick={() => setBootRetry((value) => value + 1)} className="rounded bg-blue-600 px-4 py-2 text-white">Retry</button>
      </div>
    )
  }

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500">Loading...</p>
      </div>
    )
  }

  if (needsLogin) {
    return (
      <LoginView
        onSuccess={handleLoginSuccess}
        passwordResetEnabled={Boolean(systemInfo?.password_reset_enabled)}
        registrationEnabled={systemInfo?.registration_enabled !== false}
        appleSignInEnabled={Boolean(systemInfo?.apple_sign_in_enabled)}
      />
    )
  }

  const isMultiUser = systemInfo?.mode === 'multi'
  const liveAnnouncement = civicError
    ? `Sync error: ${civicError}`
    : civicBusy
      ? 'Syncing civic data.'
      : settingsError || ''
  const secondaryFallback = location.pathname.startsWith('/profile/') ? '/profile' : '/'
  const homeLocationSubtitle = addressKnown
    ? [userSettings?.address.city, userSettings?.address.state].filter(Boolean).join(', ') || userSettings?.districts?.[0]?.name
    : undefined
  const topBarSubtitle = location.pathname === '/' ? homeLocationSubtitle || currentMeta.subtitle : currentMeta.subtitle
  const appOwnedBack = shellOwnerKey
    && location.pathname !== '/profile/values/questionnaire'
    && !location.pathname.startsWith('/bills/')
    && !location.pathname.startsWith('/representatives/')
    && !location.pathname.startsWith('/actions/')
    ? <SessionRouteBack ownerKey={shellOwnerKey} fallback={secondaryFallback} provenance={provenanceRef.current} />
    : null

  return (
    <div className="min-h-screen bg-transparent">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:rounded-md focus:bg-blue-600 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg"
      >
        Skip to main content
      </a>

      <RouteBackProvider>
        <RouteProvenanceProvider session={provenanceRef.current}>
        <ApiSessionProvider key={renderedSession ? `${renderedSession.userId}:${renderedSession.generation}` : 'single'} session={renderedSession}>
        {!shell.focused && <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          districtSummary={sidebarDistrictSummary}
          districtStatus={userSettings?.lookup_status}
          displayName={userSettings?.display_name}
          mode={systemInfo?.mode}
          pendingCTACount={ctaSummary?.pending_count ?? 0}
          unreadNotificationCount={notificationSummary?.unread_count}
          onLogout={handleLogout}
        />}

      {!shell.focused && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className={shell.focused ? '' : 'lg:pl-60'}>
        <TopBar
          title={currentMeta.title}
          subtitle={topBarSubtitle}
          onMenuClick={shell.leadingControl === 'menu' ? () => setSidebarOpen(true) : undefined}
          leftElement={shellOwnerKey && shell.leadingControl !== 'menu' ? <ShellBackButton ownerKey={shellOwnerKey} label={shell.leadingControl === 'close' ? 'Close' : 'Back'} /> : undefined}
          rightElement={
            isMultiUser && !shell.focused ? (
              <div className="flex items-center gap-2">
                <Link
                  to="/notifications"
                  aria-current={location.pathname === '/notifications' ? 'page' : undefined}
                  className={`relative flex h-12 w-12 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${location.pathname === '/notifications' ? 'bg-blue-50 text-[color:var(--accent-primary)]' : 'text-slate-600 hover:bg-slate-100'}`}
                  aria-label={notificationSummary?.unread_count == null ? 'Notifications, unread count unavailable' : `${notificationSummary.unread_count} unread notifications`}
                >
                  <Icon name="notifications" className="h-6 w-6" />
                  {notificationSummary?.unread_count ? <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-rose-600" /> : null}
                </Link>
                <Link to="/profile" className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700 hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label="Profile">
                  {(userSettings?.display_name || 'U').split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
                </Link>
              </div>
            ) : undefined
          }
        />
        <div role="status" aria-live="polite" className="sr-only">{liveAnnouncement}</div>
        <main
          ref={mainRef}
          id="main-content"
          tabIndex={-1}
          className={shell.focused ? 'mx-auto max-w-[1500px] p-4 focus:outline-none' : 'mx-auto max-w-[1500px] p-4 pb-24 sm:p-6 sm:pb-24 lg:p-8 lg:pb-8 focus:outline-none'}
        >
          <ErrorBoundary resetKey={`${location.pathname}${location.search}`}>
            <Suspense fallback={<RouteLoadingFallback />}>
              {appOwnedBack}
              {shellOwnerKey && location.pathname === '/profile/values/questionnaire' ? (
                <QuestionnaireRouteBack ownerKey={shellOwnerKey} provenance={provenanceRef.current} />
              ) : null}
              <Routes>
              <Route
                path="/"
                element={(
                  <DashboardView
                    addressKnown={addressKnown}
                    onSetAddress={() => { navigate('/profile'); setShowAddressEditor(true) }}
                    isMultiUser={isMultiUser}
                    civicCounts={civicCounts}
                    civicBusy={civicBusy}
                    civicError={civicError}
                    civicSyncStatus={civicSyncStatus}
                    civicSyncRuns={civicSyncRuns}
                    onSyncCivic={() => { void handleSyncCivic('manual') }}
                    analysisStatus={analysisStatus}
                    ctaSummary={ctaSummary}
                    notificationSummary={notificationSummary}
                    valuesComplete={Boolean(questionnaireResult?.questionnaire_completed_at && !questionnaireResult.needs_retake)}
                  />
                )}
              />

              <Route
                path="/actions"
                element={<CTAListView onOpenCTADetail={(id) => navigate(`/actions/${encodeURIComponent(id)}`)} onMutation={refreshCTASummaryAfterMutation} />}
              />
              <Route
                path="/actions/:ctaId"
                element={<CtaDetailRoute onMutation={refreshCTASummaryAfterMutation} />}
              />

              <Route
                path="/bills"
                element={(
                  <BillListView
                    civicBusy={civicBusy}
                    civicSyncStatus={civicSyncStatus}
                    userSettings={userSettings}
                    eligibleProviders={districtContext?.eligible_providers ?? []}
                    isMultiUser={isMultiUser}
                  />
                )}
              />
              <Route path="/bills/:billId" element={<BillDetailRoute />} />
              <Route path="/bills/:billId/roll-call/:voteId" element={<RollCallRoute />} />

              <Route
                path="/representatives"
                element={<RepresentativeListView addressKnown={addressKnown} />}
              />
              <Route path="/representatives/:memberId" element={<RepresentativeDetailRoute />} />

              <Route path="/nominations" element={<NominationsView />} />
              <Route
                path="/committees"
                element={<CommitteesView onOpenRepresentative={(id) => navigate(`/representatives/${encodeURIComponent(id)}`)} />}
              />
              <Route path="/votes" element={<VotesView />} />
              <Route path="/calendar" element={<CalendarView />} />
              <Route
                path="/research"
                element={<ResearchView onOpenBillDetail={openBillWithInAppOrigin} />}
              />

              <Route
                path="/profile/values/questionnaire"
                element={(
                  <QuestionnaireFlowView
                    definition={questionnaireDefinition}
                    answers={questionnaireAnswers}
                    baselineResponses={questionnaireBaseline}
                    loading={!questionnaireDefinition && !questionnaireError}
                    loadError={questionnaireError}
                    isMultiUser={isMultiUser}
                    dirtyIds={questionnaireDirtyIds}
                    skippedIds={questionnaireSkippedIds}
                    externalConflict={questionnaireExternalConflict}
                    recoveryUnavailableInitially={questionnaireRecoveryUnavailable}
                    isOwnerCurrent={questionnaireOwnerIsCurrent}
                    onWorkingChange={(answers, dirtyIds, skippedIds) => {
                      setQuestionnaireAnswers(questionnaireDefinition ? validQuestionnaireAnswers(questionnaireDefinition, answers) : answers)
                      setQuestionnaireDirtyIds(dirtyIds)
                      setQuestionnaireSkippedIds(skippedIds)
                    }}
                    onReload={() => { void loadQuestionnaire() }}
                    onResolveExternalConflict={resolveQuestionnaireExternalConflict}
                    onSaved={(result, answers, refreshed, refreshedCtaSummary) => {
                      if (!questionnaireOwnerIsCurrent(renderedSession)) return
                      const nextAnswers = questionnaireDefinition ? validQuestionnaireAnswers(questionnaireDefinition, answers) : answers
                      setQuestionnaireAnswers(nextAnswers)
                      setQuestionnaireDirtyIds(new Set())
                      setQuestionnaireSkippedIds(new Set())
                      setQuestionnaireResult(result)
                      setQuestionnaireBaseline(refreshed ?? { answers: answers as Record<string, number>, result })
                      if (refreshedCtaSummary) setCtaSummary(refreshedCtaSummary)
                      setUserSettings((prev) => prev ? { ...prev, ideology: { ...prev.ideology, score: result.score ?? null, label: result.label ?? null, questionnaire_completed_at: result.questionnaire_completed_at ?? null, questionnaire_version: result.questionnaire_version ?? result.version ?? null, answers: answers as Record<string, number> } } : prev)
                      if (renderedSession && questionnaireDefinition) {
                        const submittedAt = publishQuestionnaireSubmitInvalidation(renderedSession.userId, questionnaireDefinition.version)
                        if (submittedAt !== null) questionnaireSignalSeenRef.current = submittedAt
                      }
                      setIsEditingQuestionnaire(false)
                    }}
                  />
                )}
              />

              <Route
                path="/profile/*"
                element={(
                  <ProfileView
                    theme={themeResolution.theme}
                    themePreference={themeResolution.followsSystem ? 'system' : themeResolution.theme}
                    onThemeChange={handleThemeChange}
                    entitlement={entitlement}
                    userSettings={userSettings}
                    districtContext={districtContext}
                    profileNameInput={profileNameInput}
                    setProfileNameInput={setProfileNameInput}
                    profileBusy={profileBusy}
                    profileMessage={profileMessage}
                    profileError={profileError}
                    onSaveProfileName={() => { void handleSaveProfileName() }}
                    questionnaireDefinition={questionnaireDefinition}
                    questionnaireAnswers={questionnaireAnswers}
                    questionnaireResult={questionnaireResult}
                    questionnaireBusy={questionnaireBusy}
                    questionnaireError={questionnaireError}
                    questionnaireExternalConflict={questionnaireExternalConflict}
                    onQuestionAnswerChange={handleQuestionAnswerChange}
                    onSubmitQuestionnaire={handleSubmitQuestionnaire}
                    onResetQuestionnaire={handleResetQuestionnaire}
                    isEditingQuestionnaire={isEditingQuestionnaire}
                    onCancelEditQuestionnaire={discardQuestionnaireEdits}
                    onResolveQuestionnaireConflict={resolveQuestionnaireExternalConflict}
                    addressKnown={addressKnown}
                    showAddressEditor={showAddressEditor}
                    setShowAddressEditor={setShowAddressEditor}
                    addressForm={addressForm}
                    onAddressChange={handleAddressChange}
                    onSaveAddress={() => { void handleSaveAddress() }}
                    onCancelAddress={handleCancelAddress}
                    addressPrefillEnabled={Boolean(systemInfo?.address_prefill_enabled)}
                    settingsBusy={settingsBusy}
                    settingsError={settingsError}
                    geolocationBusy={geolocationBusy}
                    geolocationError={geolocationError}
                    geolocationWarning={geolocationWarning}
                    geolocationSuccess={geolocationSuccess}
                    onDetectLocation={handleDetectLocation}
                    isMultiUser={isMultiUser}
                    onLogout={handleLogout}
                    onDeleteAccount={handleDeleteAccount}
                    accountBusy={accountBusy}
                    accountError={accountError}
                    hasPassword={hasPassword}
                    onChangePassword={(currentPassword, newPassword) => { void handleChangePassword(currentPassword, newPassword) }}
                  />
                )}
              />

              <Route
                path="/settings/*"
                element={(
                  <SettingsView
                    userSettings={userSettings}
                    settingsBusy={settingsBusy}
                    settingsError={settingsError}
                    congressApiKeyInput={congressApiKeyInput}
                    setCongressApiKeyInput={setCongressApiKeyInput}
                    onSaveCongressApiKey={() => { void handleSaveCongressApiKey() }}
                    onClearCongressApiKey={() => { void handleClearCongressApiKey() }}
                    civicSettingsMessage={civicSettingsMessage}
                    autoSyncEnabled={autoSyncEnabled}
                    onToggleAutoSync={(enabled: boolean) => { void handleToggleAutoSync(enabled) }}
                    syncIntervalMinutes={systemInfo?.sync_interval_minutes ?? 1440}
                    isMultiUser={systemInfo?.mode === 'multi'}
                    llmSettings={llmSettings}
                    llmProviderInput={llmProviderInput}
                    setLlmProviderInput={setLlmProviderInput}
                    llmEndpointInput={llmEndpointInput}
                    setLlmEndpointInput={setLlmEndpointInput}
                    llmModelInput={llmModelInput}
                    setLlmModelInput={setLlmModelInput}
                    llmApiKeyInput={llmApiKeyInput}
                    setLlmApiKeyInput={setLlmApiKeyInput}
                    llmBusy={llmBusy}
                    llmMessage={llmMessage}
                    llmError={llmError}
                    onSaveLLMSettings={() => { void handleSaveLLMSettings() }}
                    onTestLLMConnection={() => { void handleTestLLMConnection() }}
                  />
                )}
              />

              <Route path="/badges" element={<BadgesView />} />

              <Route path="/topics" element={<TopicsView />} />
              <Route
                path="/notifications"
                element={(
                  <NotificationsView
                    summaryCoordinator={notificationCoordinator}
                  />
                )}
              />

              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
        <MobileNav hidden={!shell.bottomNav} />
        </ApiSessionProvider>
        </RouteProvenanceProvider>
      </RouteBackProvider>
    </div>
  )
}
